/**
 * DOM Patch for React compatibility with external DOM modifications.
 *
 * This patch prevents React crashes caused by external scripts (browser extensions,
 * translation tools, Samsung Internet features, etc.) that modify the DOM outside
 * of React's control.
 *
 * When external code removes or moves DOM nodes that React is tracking, React's
 * reconciler throws errors like:
 * - "NotFoundError: Failed to execute 'removeChild' on 'Node'"
 * - "NotFoundError: Failed to execute 'insertBefore' on 'Node'"
 *
 * This patch wraps the native DOM methods to gracefully handle these cases
 * instead of crashing the application.
 *
 * @see https://github.com/facebook/react/issues/11538
 */

export function patchDomForReactCompatibility(): void {
  if (typeof window === "undefined" || typeof Node === "undefined") {
    return;
  }

  // Store original methods
  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  // Patch removeChild to handle nodes that were already removed by external scripts
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      // Node was already removed or moved by something external to React.
      // Return the child to satisfy the API contract without throwing.
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "[DOM Patch] Attempted to remove a child that is not a child of this node. " +
            "This is likely caused by a browser extension or external script modifying the DOM."
        );
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  // Patch insertBefore to handle reference nodes that were removed by external scripts
  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node was removed or moved by something external to React.
      // Fall back to appendChild behavior.
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "[DOM Patch] Attempted to insert before a reference node that is not a child of this node. " +
            "This is likely caused by a browser extension or external script modifying the DOM."
        );
      }
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
